import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectParticipantAttendedModules } from '../../../../store/courseSelectors';
import {
    loadPreviouslySuppliedFibreSupplements,
    loadRescuePackageDeclinedReasonTypes,
    loadRescuePackageMedicationConfirmationDetails,
    loadRescuePackageOfferedDetails,
    loadRescuePackageTypes,
    loadTDRConsumptionDetails
} from '../../../../store/directusService';
import {
    clearT2DRHealthDataEntries,
    loadT2DRHealthDataEntries
} from '../../../../store/participantService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    PREVIOUSLY_SUPPLIED_FIBRE_SUPPLEMENTS_ERROR,
    RESCUE_PACKAGE_DECLINED_REASON_TYPES_ERROR,
    RESCUE_PACKAGE_MEDICATION_CONFIRMATION_DETAILS_ERROR,
    RESCUE_PACKAGE_OFFERED_DETAILS_ERROR,
    RESCUE_PACKAGE_TYPES_ERROR,
    T2DR_CONSUMPTION_DETAILS_ERROR,
    T2DR_HEALTH_DATA_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import EditT2DRHealthData from './EditT2DRHealthData';
import T2DRHealthDataRow from './T2DRHealthDataRow';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'tdrConsumption', numeric: false, label: 'TDR Consumption' },
    { id: 'rescuePackageOffered', numeric: false, label: 'Rescue Package Offered' },
    { id: 'rescuePackageDeclinedReason', numeric: false, label: 'Rescue Package Declined Reason' },
    { id: 'rescuePackageType', numeric: false, label: 'Rescue Package Type' },
    {
        id: 'rescuePackageMedicationConfirmation',
        numeric: false,
        label: 'Rescue Package Medication Confirmed'
    },
    { id: 'tdrDaysMissed', numeric: true, label: 'TDR Days Missed' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'tdrConsumption',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const T2DRHealthData = ({ participant, linkedModuleId, isCreateDisabled, isEditDisabled }) => {
    const dispatch = useDispatch();
    const t2drHealthDataRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [firstEntry, setFirstEntry] = useState({});
    const [modules, setModules] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const attendedModules = useSelector(selectParticipantAttendedModules);
    const t2drHealthDataEntries = useSelector(
        (state) => state.entities.participantService.t2drHealthDataEntries
    );
    const t2drHealthDataEntriesMetaData = useSelector(
        (state) => state.entities.participantService.t2drHealthDataEntriesMetaData
    );
    const tdrConsumptionDetails = useSelector(
        (state) => state.entities.directusService.tdrConsumptionDetails
    );
    const previouslySuppliedFibreSupplements = useSelector(
        (state) => state.entities.directusService.previouslySuppliedFibreSupplements
    );
    const rescuePackageOfferedDetails = useSelector(
        (state) => state.entities.directusService.rescuePackageOfferedDetails
    );
    const rescuePackageTypes = useSelector(
        (state) => state.entities.directusService.rescuePackageTypes
    );
    const rescuePackageDeclinedReasonTypes = useSelector(
        (state) => state.entities.directusService.rescuePackageDeclinedReasonTypes
    );
    const rescuePackageMedicationConfirmationDetails = useSelector(
        (state) => state.entities.directusService.rescuePackageMedicationConfirmationDetails
    );

    // USEEFFECTS
    useEffect(() => {
        if (!tdrConsumptionDetails?.length) dispatch(loadTDRConsumptionDetails());
        if (!previouslySuppliedFibreSupplements?.length)
            dispatch(loadPreviouslySuppliedFibreSupplements());
        if (!rescuePackageOfferedDetails?.length) dispatch(loadRescuePackageOfferedDetails());
        if (!rescuePackageTypes?.length) dispatch(loadRescuePackageTypes());
        if (!rescuePackageDeclinedReasonTypes?.length)
            dispatch(loadRescuePackageDeclinedReasonTypes());
        if (!rescuePackageMedicationConfirmationDetails?.length)
            dispatch(loadRescuePackageMedicationConfirmationDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!participant?.id) return;
        dispatch(loadT2DRHealthDataEntries(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (t2drHealthDataEntriesMetaData?.loaded) {
            let modules = [];
            if (!t2drHealthDataEntries?.length) {
                setRows([]);
                if (attendedModules?.length) modules = [...attendedModules];
                setLoadingContentMessage(T2DR_HEALTH_DATA_ERROR);
            } else {
                const first = t2drHealthDataEntries.find((el) => el.entryNumber === 1) || {};
                setFirstEntry(first);
                const rows = t2drHealthDataEntries.map((el) => ({
                    ...el,
                    actualTdrStartDate: first?.actualTdrStartDate || '',
                    tdrConsumption: getNameFromId(tdrConsumptionDetails, el.tdrConsumptionId),
                    rescuePackageOffered: getNameFromId(
                        rescuePackageOfferedDetails,
                        el.rescuePackageOfferedId
                    ),
                    rescuePackageDeclinedReason: getNameFromId(
                        rescuePackageDeclinedReasonTypes,
                        el.rescuePackageDeclinedReasonId
                    ),
                    rescuePackageType: getNameFromId(rescuePackageTypes, el.rescuePackageTypeId),
                    rescuePackageMedicationConfirmation: getNameFromId(
                        rescuePackageMedicationConfirmationDetails,
                        el.rescuePackageMedicationConfirmationId
                    ),
                    previouslySuppliedFibreSupplements: getNameFromId(
                        previouslySuppliedFibreSupplements,
                        el.previouslySuppliedFibreSupplementsId
                    ),
                    linkedModule:
                        attendedModules?.find((entry) => entry.id === el.linkedModuleId)?.title ||
                        undefined
                }));
                setRows(rows);

                if (attendedModules?.length) {
                    modules = attendedModules.filter(
                        (el) =>
                            !t2drHealthDataEntries.find((entry) => entry.linkedModuleId === el.id)
                    );
                }
            }
            setModules(modules.map((el) => ({ ...el, name: el.title })) || []);
        }
    }, [t2drHealthDataEntries, t2drHealthDataEntriesMetaData, attendedModules]);

    // FNs
    const loadingError = () => {
        if (!tdrConsumptionDetails?.length) return T2DR_CONSUMPTION_DETAILS_ERROR;
        if (!previouslySuppliedFibreSupplements?.length)
            return PREVIOUSLY_SUPPLIED_FIBRE_SUPPLEMENTS_ERROR;
        if (!rescuePackageOfferedDetails?.length) return RESCUE_PACKAGE_OFFERED_DETAILS_ERROR;
        if (!rescuePackageTypes?.length) return RESCUE_PACKAGE_TYPES_ERROR;
        if (!rescuePackageDeclinedReasonTypes?.length)
            return RESCUE_PACKAGE_DECLINED_REASON_TYPES_ERROR;
        if (!rescuePackageMedicationConfirmationDetails?.length)
            return RESCUE_PACKAGE_MEDICATION_CONFIRMATION_DETAILS_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <T2DRHealthDataRow
                key={el.id}
                row={el}
                isDisabled={isEditDisabled || showForm}
                onEdit={onEdit}
                toggleDropdown={setOpenId}
                openId={openId}
            />
        ));

    const cleanUp = () => {
        dispatch(clearT2DRHealthDataEntries());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(t2drHealthDataRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${local.wellBeingSection} ${app.fadein}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    // RENDER
    return (
        <div
            className={local.wellBeingSection}
            data-testid="t2dr_health_data"
            ref={t2drHealthDataRef}>
            <h2 className={app.sectionHeading}>T2DR Health Data</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addT2DRHealthDataButton"
                        type="button"
                        disabled={isCreateDisabled}
                        content="Add New T2DR Health Data"
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <EditT2DRHealthData
                        participant={participant}
                        onClose={onClose}
                        isDisabled={
                            (formType === 'edit' && isEditDisabled) ||
                            (formType === 'create' && isCreateDisabled)
                        }
                        attendedModules={modules}
                        linkedModuleId={linkedModuleId || ''}
                        formType={formType}
                        row={selectedRow}
                        firstEntry={firstEntry}
                    />
                )}
            </div>
        </div>
    );
};

T2DRHealthData.propTypes = {
    participant: PropTypes.object,
    linkedModuleId: PropTypes.string,
    isCreateDisabled: PropTypes.bool,
    isEditDisabled: PropTypes.bool
};

export default T2DRHealthData;
